<script setup>
import { i18n } from 'boot/i18n';
import {
  HrErrorBanner,
  HrList,
  HrListAddBtn,
  HrListItem,
  HrListSectionHeader,
  useDialog,
} from 'src/design-system';
import { computed, ref, watch } from 'vue';

import { AUTHORIZATION_SESSION_METHODS } from '../composables/useClientAuthorizations';
import { presentAuthorizationRow } from '../presenters/authorizationRowPresenter';
import { selectableFundingSources } from '../presenters/selectableFundingSources';
import AuthorizationList from './AuthorizationList.vue';
import EditAuthorizationDialog from './EditAuthorizationDialog.vue';

/**
 * The Revenue Cycle context's published interface into the client dialog: record what a funder
 * authorized, so services under a require-auth payer can bill instead of holding.
 *
 * A PANEL, not a tab: the host owns the tab label and the panel wrapper.
 *
 * The session is OWNED BY THE HOST and passed in, because the wrapper unmounts on tab switch and
 * staged edits have to outlive that — and the flush happens after the client is saved, which only
 * the host knows about. Both halves have to be used together.
 *
 * TREATMENT SERVICES AND PLACES OF SERVICE ARRIVE AS PROPS; FUNDING SOURCES DO NOT. The first two
 * are clinic config the host already holds for the neighboring tabs. Funders are effective-dated
 * and have to be resolved as-of a date the session owns, so the session loads them.
 */
defineOptions({ name: 'ClientAuthorizationPanel' });

const props = defineProps({
  /**
   * The editing session from `useClientAuthorizations`, created and owned by the host.
   * @type {import('../composables/useClientAuthorizations').AuthorizationSession}
   */
  authorizations: {
    type: Object,
    required: true,
    validator: (session) => AUTHORIZATION_SESSION_METHODS.every((method) => {
      return typeof session?.[method] === 'function';
    }),
  },
  /** The client whose authorizations these are; null while the client is being created. */
  clientId: {
    type: String,
    default: null,
  },
  clinicId: {
    type: String,
    required: true,
  },
  /** The clinic's treatment services. */
  treatmentServices: {
    type: Array,
    default: () => [],
  },
  /** The clinic's places of service, for the setting scope. */
  placesOfService: {
    type: Array,
    default: () => [],
  },
  /**
   * The places of service could not be loaded. Passed on rather than inferred from an empty list: a
   * clinic with none configured is the ordinary case and reads identically here.
   */
  placesOfServiceUnavailable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['error']);

const { confirmDestructiveDialog } = useDialog();

const editDialog = ref(null);

/** The authorization the dialog is open on, or null when adding. The payer picker depends on it. */
const editing = ref(null);

// Built from the UNFILTERED list on purpose. A recorded authorization must still be able to name the
// payer it was written against, and a payer retired afterwards would otherwise render as "Unknown
// funding source" on a row that is perfectly valid.
const fundingSourceNameById = computed(() => {
  return new Map(props.authorizations.fundingSources.value.map((funder) => [funder.fundingSourceId, funder.name]));
});

/**
 * CODE FIRST, then the clinical name. A funder's letter identifies what it approved by procedure
 * code, so a biller transcribing one has 97153 in hand rather than "Direct service", and a picker
 * offering only clinical names makes them translate before they can find the row.
 */
const treatmentServiceNameById = computed(() => {
  return new Map(props.treatmentServices.map((service) => [
    service.id,
    [service.serviceCode, service.serviceName].filter(Boolean).join(' - '),
  ]));
});

const rows = computed(() => {
  return props.authorizations.authorizations.value.map((authorization) => {
    return presentAuthorizationRow(authorization, {
      fundingSourceNameById: fundingSourceNameById.value,
      treatmentServiceNameById: treatmentServiceNameById.value,
      asOf: props.authorizations.asOf,
    });
  });
});

/**
 * The payers a new authorization may name, in the order the picker offers them.
 *
 * Alphabetical, with RETIRED PAYERS EXCLUDED — shared with the coverage picker so the two agree.
 *
 * `keepId` KEEPS THE PAYER THE RECORD BEING EDITED ALREADY NAMES. Without it, opening a letter
 * written against a payer retired since finds no matching option, shows a blank where the funder
 * was, and saves the erasure. Letters run for a year and clinics retire payers mid-year, so this is
 * ordinary rather than odd. Null while adding.
 */
const fundingSourceOptions = computed(() => {
  return selectableFundingSources(
    props.authorizations.fundingSources.value,
    editing.value?.fundingSourceId ?? null,
  ).map((funder) => ({
    value: funder.fundingSourceId,
    label: funder.name,
  }));
});

/** Scoped to the funder, because a letter can only approve what that payer covers. */
const serviceOptionsByFundingSource = computed(() => {
  return props.authorizations.fundingSources.value.reduce((options, funder) => {
    return {
      ...options,
      [funder.fundingSourceId]: (funder.coveredServices ?? [])
        .map(({ treatmentServiceId }) => ({
          value: treatmentServiceId,
          label: treatmentServiceNameById.value.get(treatmentServiceId),
        }))
        // A covered service whose treatment service the clinic has since deleted has no name to
        // show, so it is dropped rather than offered as a blank row.
        .filter(({ label }) => label !== undefined)
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, {});
});

/**
 * The DISTINCT place-of-service CODES this clinic uses. A place of service is clinic-authored with
 * a free-string code, and two records ("School A", "School B") can legitimately share one — the
 * letter scopes the code, so that is what the option carries.
 */
const settingOptions = computed(() => {
  const byCode = new Map();

  props.placesOfService.forEach((place) => {
    if (place?.placeOfServiceCode && !byCode.has(place.placeOfServiceCode)) {
      byCode.set(place.placeOfServiceCode, place.placeOfServiceName ?? place.placeOfServiceCode);
    }
  });

  return [...byCode.entries()].map(([value, label]) => ({ value, label: `${label} (${value})` }));
});

const treatmentServiceName = (treatmentServiceId) => {
  return treatmentServiceNameById.value.get(treatmentServiceId)
    ?? i18n.t('billingAuthorizations.unknownService');
};

/** A client usually has one or two live letters; more rows would promise a longer list than most have. */
const LOADING_ROWS = 2;

const loadFailed = ref(false);

const isEmpty = computed(() => {
  return !props.authorizations.isLoading.value && !loadFailed.value && rows.value.length === 0;
});

async function loadAuthorizations() {
  loadFailed.value = false;
  try {
    await props.authorizations.load({ clientId: props.clientId, clinicId: props.clinicId });
  } catch (error) {
    // Never fall through to the empty state: "none recorded" and "we could not read them" look
    // identical, and the first invites a biller to conclude a service is unauthorized when it is
    // not.
    loadFailed.value = true;
    emit('error', error);
  }
} // loadAuthorizations()

watch(() => props.clientId, loadAuthorizations, { immediate: true });

/** One handler for both: the dialog reports which record it was editing, or null for a new one. */
const onRecorded = ({ authorizationId, draft }) => {
  return authorizationId === null
    ? props.authorizations.add(draft)
    : props.authorizations.update(authorizationId, draft);
};

const onEdit = (authorizationId) => {
  const authorization = props.authorizations.authorizations.value
    .find((entry) => entry.authorizationId === authorizationId);

  // Checked here as well as in the row. `readonly` hides controls, and hiding is not refusing.
  if (authorization && !props.readonly) {
    editing.value = authorization;
    editDialog.value?.show(authorization);
  }
};

const onAdd = () => {
  if (props.readonly) {
    return;
  }
  editing.value = null;
  editDialog.value?.show();
};

/**
 * Removal is right for a letter recorded in error, which is what the copy steers to. One that was
 * real and has run out should be left alone: it keeps clearing holds for dates inside its window.
 */
function onRemove(authorizationId) {
  if (props.readonly) {
    return;
  }

  // POSITIONAL, and `confirmLabel` is required — the helper returns without opening anything when
  // `onOk` is missing, so an options object silently made removal a no-op.
  confirmDestructiveDialog(
    i18n.t('billingAuthorizations.titleRemove'),
    i18n.t('billingAuthorizations.messageRemove'),
    () => props.authorizations.remove(authorizationId),
    { confirmLabel: i18n.t('common.buttonDelete') },
  );
} // onRemove()
</script>

<template>
  <div>
    <!-- Placeholder rows rather than a spinner, matching the coverage panel: the section header is
         real and arrives immediately, so the list has a shape before the data does, and rows
         resolving in place is less movement than a spinner being replaced by a list. `caption` is
         set so each reserves the second line the real rows use for their window. -->
    <hr-list v-if="authorizations.isLoading.value">
      <hr-list-section-header>
        {{ i18n.t('billingAuthorizations.sectionTitle') }}
      </hr-list-section-header>
      <hr-list-item
        v-for="index in LOADING_ROWS"
        :key="index"
        segmented
        pending
        label=""
        caption=""
      />
    </hr-list>

    <hr-error-banner
      v-else-if="loadFailed"
      :message="i18n.t('billingAuthorizations.loadFailed')"
    />

    <hr-list v-if="!authorizations.isLoading.value && !loadFailed">
      <hr-list-section-header>
        {{ i18n.t('billingAuthorizations.sectionTitle') }}
      </hr-list-section-header>

      <authorization-list
        v-if="!isEmpty"
        :rows="rows"
        :readonly="readonly"
        @edit="onEdit"
        @remove="onRemove"
      />

      <!-- With no authorizations this button IS the empty state, as it is on the coverage panel. -->
      <hr-list-add-btn
        v-if="!readonly"
        :variant="isEmpty ? 'outlined' : 'flat'"
        :label="i18n.t('billingAuthorizations.actionAdd')"
        data-e2e="btn-add-authorization"
        @click="onAdd"
      />
    </hr-list>

    <edit-authorization-dialog
      ref="editDialog"
      :funding-source-options="fundingSourceOptions"
      :service-options-by-funding-source="serviceOptionsByFundingSource"
      :setting-options="settingOptions"
      :settings-unavailable="placesOfServiceUnavailable"
      :validate="authorizations.validate"
      :treatment-service-name="treatmentServiceName"
      @ok="onRecorded"
    />
  </div>
</template>
